/**
 * waap-analytics-tab.tsx — WAAP Analytics tab: 7 sub-boards (read-only).
 *
 * Sub-boards (all chart-only, no mutations):
 *   monitoring | delivery | security | aiwaf | bot | api | threat
 *
 * Each board: time-range picker + optional domain multi-select + chart grid.
 * Threat Investigation additionally has a full sidebar filter panel and a
 * per-row log-detail modal (see waap-threat-investigation-board.tsx).
 *
 * Data source: actAsClient → GET /waap/domains/:anchorId/analytics/<board>/charts
 * All responses: { charts: WaapChart[] } (same discriminated union as Dashboard).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart3, Truck, ShieldAlert, Brain, Bot, Boxes,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@vnetwork/ui/primitives';
import {
  MONITORING_TIME_RANGES, type MonitoringTimeRange,
  DELIVERY_TIME_RANGES,  type DeliveryTimeRange,
  SECURITY_TIME_RANGES,  type SecurityTimeRange,
  AIWAF_TIME_RANGES,     type AiWafTimeRange,
  BOT_TIME_RANGES,       type BotTimeRange,
  API_TIME_RANGES,       type ApiTimeRange,
  THREAT_TIME_RANGES,
  useActAsWAAPMonitoringCharts,
  useActAsWAAPDeliveryCharts,
  useActAsWAAPSecurityCharts,
  useActAsWAAPAiWafCharts,
  useActAsWAAPBotCharts,
  useActAsWAAPApiCharts,
} from '@/api/act-as-waap';
import { WaapAnalyticsBoard }         from './waap-analytics-board';
import { WaapThreatInvestigationBoard } from './waap-threat-investigation-board';

/* ── Sub-board registry ──────────────────────────────────────────────────── */

type AnalyticsBoard = 'monitoring' | 'delivery' | 'security' | 'aiwaf' | 'bot' | 'api' | 'threat';

interface BoardMeta {
  label: string;
}

const BOARD_META: Record<AnalyticsBoard, BoardMeta> = {
  monitoring: { label: 'Monitoring' },
  delivery:   { label: 'Delivery Overview' },
  security:   { label: 'Security Overview' },
  aiwaf:      { label: 'AI-WAF' },
  bot:        { label: 'Bot Management' },
  api:        { label: 'API' },
  threat:     { label: 'Threat Investigation' },
};

const BOARDS: AnalyticsBoard[] = ['monitoring', 'delivery', 'security', 'aiwaf', 'bot', 'api', 'threat'];

/* ── Individual board renderers ──────────────────────────────────────────── */

function MonitoringBoard({ anchorId }: { anchorId: string }) {
  const [range, setRange] = useState<MonitoringTimeRange>('Last 12 Hours');
  const [domains, setDomains] = useState<string[]>([]);
  const query = useActAsWAAPMonitoringCharts(anchorId, range, domains);
  return (
    <WaapAnalyticsBoard
      icon={<BarChart3 size={22} />}
      label="monitoring"
      ranges={MONITORING_TIME_RANGES}
      range={range}
      onRangeChange={setRange}
      domains={domains}
      onDomainsChange={setDomains}
      anchorId={anchorId}
      query={query}
    />
  );
}

function DeliveryBoard({ anchorId }: { anchorId: string }) {
  const [range, setRange] = useState<DeliveryTimeRange>('Last 4 hours');
  const [domains, setDomains] = useState<string[]>([]);
  const query = useActAsWAAPDeliveryCharts(anchorId, range, domains);
  return (
    <WaapAnalyticsBoard
      icon={<Truck size={22} />}
      label="delivery"
      ranges={DELIVERY_TIME_RANGES}
      range={range}
      onRangeChange={setRange}
      domains={domains}
      onDomainsChange={setDomains}
      anchorId={anchorId}
      query={query}
    />
  );
}

function SecurityBoard({ anchorId }: { anchorId: string }) {
  const [range, setRange] = useState<SecurityTimeRange>('Last 4 hours');
  const [domains, setDomains] = useState<string[]>([]);
  const query = useActAsWAAPSecurityCharts(anchorId, range, domains);
  return (
    <WaapAnalyticsBoard
      icon={<ShieldAlert size={22} />}
      label="security"
      ranges={SECURITY_TIME_RANGES}
      range={range}
      onRangeChange={setRange}
      domains={domains}
      onDomainsChange={setDomains}
      anchorId={anchorId}
      query={query}
    />
  );
}

function AiWafBoard({ anchorId }: { anchorId: string }) {
  const [range, setRange] = useState<AiWafTimeRange>('Last 24 Hours');
  const [domains, setDomains] = useState<string[]>([]);
  const query = useActAsWAAPAiWafCharts(anchorId, range, domains);
  return (
    <WaapAnalyticsBoard
      icon={<Brain size={22} />}
      label="AI-WAF"
      ranges={AIWAF_TIME_RANGES}
      range={range}
      onRangeChange={setRange}
      domains={domains}
      onDomainsChange={setDomains}
      anchorId={anchorId}
      query={query}
    />
  );
}

function BotBoard({ anchorId }: { anchorId: string }) {
  const [range, setRange] = useState<BotTimeRange>('Last 24 Hours');
  const [domains, setDomains] = useState<string[]>([]);
  const query = useActAsWAAPBotCharts(anchorId, range, domains);
  return (
    <WaapAnalyticsBoard
      icon={<Bot size={22} />}
      label="bot management"
      ranges={BOT_TIME_RANGES}
      range={range}
      onRangeChange={setRange}
      domains={domains}
      onDomainsChange={setDomains}
      anchorId={anchorId}
      query={query}
    />
  );
}

function ApiBoard({ anchorId }: { anchorId: string }) {
  const [range, setRange] = useState<ApiTimeRange>('Last 24 Hours');
  const query = useActAsWAAPApiCharts(anchorId, range);
  // API board has no domain filter (account-wide only).
  return (
    <WaapAnalyticsBoard
      icon={<Boxes size={22} />}
      label="API"
      ranges={API_TIME_RANGES}
      range={range}
      onRangeChange={setRange}
      anchorId={anchorId}
      query={query}
    />
  );
}

/* ── Props ───────────────────────────────────────────────────────────────── */

interface WaapAnalyticsTabProps {
  /** Anchor domain UUID for this WAAP service. */
  anchorId: string;
  /** serviceId passed for context / future extensions. */
  serviceId: string;
}

/* ── Component ───────────────────────────────────────────────────────────── */

/**
 * Analytics tab — 7 analytics sub-boards. Uses inner segmented Tabs to switch
 * boards. All boards are READ-ONLY (no mutations). Threat Investigation has an
 * additional filter sidebar and log-detail modal.
 */
export function WaapAnalyticsTab({ anchorId, serviceId: _serviceId }: WaapAnalyticsTabProps) {
  const { t } = useTranslation();
  const [active, setActive] = useState<AnalyticsBoard>('monitoring');

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* ── Board selector ───────────────────────────────────────────────── */}
      <Tabs value={active} onValueChange={(v) => setActive(v as AnalyticsBoard)}>
        <TabsList style={{ flexWrap: 'wrap' }}>
          {BOARDS.map((b) => (
            <TabsTrigger key={b} value={b}>
              {t(`waap_domain.analytics_board_${b}`, BOARD_META[b].label)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* ── Active board content ─────────────────────────────────────────── */}
      {!anchorId ? (
        <p style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)' }}>
          {t('waap_domain.analytics_no_anchor', 'No anchor domain — add a website to this WAAP service first.')}
        </p>
      ) : (
        <>
          {active === 'monitoring' && <MonitoringBoard anchorId={anchorId} />}
          {active === 'delivery'   && <DeliveryBoard   anchorId={anchorId} />}
          {active === 'security'   && <SecurityBoard   anchorId={anchorId} />}
          {active === 'aiwaf'      && <AiWafBoard      anchorId={anchorId} />}
          {active === 'bot'        && <BotBoard        anchorId={anchorId} />}
          {active === 'api'        && <ApiBoard        anchorId={anchorId} />}
          {active === 'threat'     && (
            <WaapThreatInvestigationBoard
              anchorId={anchorId}
              defaultRange={THREAT_TIME_RANGES[1]}
            />
          )}
        </>
      )}
    </div>
  );
}
